import {
  completeIntakeUploadAPI,
  uploadIntakeDocumentAPI,
} from "../../service/mailIntake";
import { uploadToSignedUrl } from "../../service/uploadService";

// Upload URLs are signed for an hour; re-sign a little before that.
const URL_FRESH_MS = 50 * 60 * 1000;
const COMPLETE_RETRY_DELAYS_MS = [2000, 5000, 10000];
export const DEFAULT_CONCURRENCY = 5;

export const defaultApi = {
  presign: (args) => uploadIntakeDocumentAPI(args),
  put: (args) => uploadToSignedUrl(args),
  complete: (itemId) => completeIntakeUploadAPI(itemId),
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const fileKey = (file) => `${file.name}|${file.size}|${file.lastModified}`;

export const isPdf = (file) =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

export const makeEntry = (file) => ({
  key: fileKey(file),
  file,
  status: "queued", // queued | uploading | done | failed
  error: null,
  itemId: null,
  uploadUrl: null,
  signedAt: 0,
  stored: false,
});

const retriable = (error) => {
  const status = error?.status ?? error?.response?.status;
  return !status || status >= 500;
};

async function completeWithRetry(api, itemId, delays) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await api.complete(itemId);
    } catch (error) {
      if (!retriable(error) || attempt >= delays.length) throw error;
      await sleep(delays[attempt]);
    }
  }
}

// One file: get an upload slot, send the bytes straight to storage, then tell
// WHG Docs it has arrived (which puts it in the sorting queue). Each step that
// already succeeded is remembered on the entry, so a retry resumes rather than
// creating a second copy.
export async function uploadOne(entry, { api = defaultApi, now = Date.now, delays = COMPLETE_RETRY_DELAYS_MS } = {}) {
  if (!entry.stored && (!entry.itemId || now() - entry.signedAt > URL_FRESH_MS)) {
    const res = await api.presign({
      filename: entry.file.name,
      file_size: entry.file.size,
      content_type: "application/pdf",
      source: "manual_upload",
    });
    entry.itemId = res.data.intake_item_id;
    entry.uploadUrl = res.data.upload_url;
    entry.signedAt = now();
  }
  if (!entry.stored) {
    await api.put({ file: entry.file, signedUrl: entry.uploadUrl, contentType: "application/pdf" });
    entry.stored = true;
  }
  await completeWithRetry(api, entry.itemId, delays);
}

// Upload every queued or failed entry, a few at a time. ``onChange`` fires
// after each entry changes status so the page can redraw its counters.
export async function runUploads(entries, { concurrency = DEFAULT_CONCURRENCY, onChange = () => {}, ...opts } = {}) {
  const todo = entries.filter((e) => e.status === "queued" || e.status === "failed");
  let next = 0;
  const worker = async () => {
    while (next < todo.length) {
      const entry = todo[next];
      next += 1;
      entry.status = "uploading";
      entry.error = null;
      onChange(entry);
      try {
        await uploadOne(entry, opts);
        entry.status = "done";
      } catch (error) {
        entry.status = "failed";
        entry.error = error?.message || "Upload failed";
      }
      onChange(entry);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, todo.length) }, worker));
}

export const tally = (entries) => {
  const counts = { total: entries.length, queued: 0, uploading: 0, done: 0, failed: 0 };
  entries.forEach((e) => {
    counts[e.status] += 1;
  });
  return counts;
};
